import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import { useSaveShowcaseSettings, useSendShowcaseGuide, useUpdateShowcaseSite } from "@/hooks/useShowcase";
import CodeBox from "./CodeBox";
import { LINE_STEPS, PLACEMENTS, PLATFORMS, PLUGIN_STEPS, platformOf, rootOf, supportEmailOf } from "./showcaseSetupModel";

const PLATFORM_OPTIONS = PLATFORMS.map((item) => ({ value: item.key, label: item.label }));

const Steps = ({ steps, note }) => (
  <>
    <ol className="showcase-setup__guide">
      {steps.map((step) => (
        <li key={step}>{step}</li>
      ))}
    </ol>
    {note ? <p className="showcase-setup__hint">{note}</p> : null}
  </>
);

const SupportEmail = ({ platform, showcase, site, brokerageName, code }) => {
  const [placement, setPlacement] = useState(PLACEMENTS[0].value);
  const email = supportEmailOf({ platform, brokerageName, host: site.host, pageUrl: site.pageUrl, placement, code });
  return (
    <div className="showcase-setup__part">
      <div className="showcase-setup__field">
        <label htmlFor="support-placement">Where on the page</label>
        <SelectDropDown
          id="support-placement"
          classNamePrefix="select"
          options={PLACEMENTS}
          value={PLACEMENTS.find((option) => option.value === placement)}
          styles={PICKER_STYLES}
          errorClass="d-none"
          onChange={(option) => option && setPlacement(option.value)}
        />
      </div>
      <a className="btn btn-primary showcase-setup__button showcase-setup__wide" href={email.mailto}>
        Email {platform.label}&nbsp;support
      </a>
      <CodeBox code={email.body} label="Copy email" />
    </div>
  );
};

const HostedOptions = ({ brokerageId, showcase }) => {
  const save = useSaveShowcaseSettings(brokerageId);
  const root = rootOf(showcase.sites?.[0]?.host || "");
  const [customHost, setCustomHost] = useState(showcase.customHost || (root ? `buyers.${root}` : ""));
  const [error, setError] = useState("");
  const label = (showcase.customHost || customHost).split(".")[0] || "buyers";

  const submit = (event) => {
    event.preventDefault();
    save.mutate(
      { customHost: customHost.trim().toLowerCase() || null },
      {
        onSuccess: () => setError(""),
        onError: (failure) => {
          const field = failure.fields?.customHost;
          setError(
            field === "notYourDomain"
              ? `Use an address under one of your websites, like buyers.${root || "yourbrokerage.com"}.`
              : field === "taken"
                ? "Another brokerage already uses that address."
                : field
                  ? "Enter an address like buyers.yourbrokerage.com."
                  : failure.message,
          );
        },
      },
    );
  };

  return (
    <>
      <h5 className="showcase-setup__subhead">Your own buyers&nbsp;page</h5>
      <form className="showcase-setup__row" onSubmit={submit} noValidate>
        <input
          value={customHost}
          onChange={(event) => {
            setCustomHost(event.target.value);
            setError("");
          }}
          placeholder={`buyers.${root || "yourbrokerage.com"}`}
          aria-label="Buyers page address"
          aria-invalid={Boolean(error)}
        />
        <button type="submit" className="showcase-setup__link" disabled={save.isPending}>
          Save
        </button>
      </form>
      {error ? (
        <p className="showcase-setup__error" role="alert">
          {error}
        </p>
      ) : null}
      {showcase.customHost ? (
        <p className="showcase-setup__hint">
          {showcase.customHostVerified
            ? showcase.customHostSeenAt
              ? `${showcase.customHost} is working.`
              : `${showcase.customHost} is connected. Open it once to confirm.`
            : "Available after we connect your domain. We email you when it is\u00a0ready."}
        </p>
      ) : null}
      {showcase.customHost && !showcase.customHostVerified ? (
        <dl className="showcase-setup__dns">
          <div>
            <dt>Type</dt>
            <dd>CNAME</dd>
          </div>
          <div>
            <dt>Name</dt>
            <dd>{label}</dd>
          </div>
          <div>
            <dt>Value</dt>
            <dd>{showcase.cnameTarget}</dd>
          </div>
        </dl>
      ) : null}
      <h5 className="showcase-setup__subhead">Or link to our hosted&nbsp;page</h5>
      <CodeBox code={showcase.hostedUrl} label="Copy link" />
    </>
  );
};

const GuideEmail = ({ brokerageId }) => {
  const sendGuide = useSendShowcaseGuide(brokerageId);
  const [email, setEmail] = useState("");
  const [sentTo, setSentTo] = useState("");
  const submit = (event) => {
    event.preventDefault();
    const to = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Enter your web person's email address.");
      return;
    }
    sendGuide.mutate(to, {
      onSuccess: () => {
        setSentTo(to);
        setEmail("");
      },
      onError: (error) => toast.error(error.message),
    });
  };
  return (
    <div className="showcase-setup__part">
      <h5 className="showcase-setup__subhead">Email the steps to your web&nbsp;person</h5>
      <form className="showcase-setup__row" onSubmit={submit} noValidate>
        <input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="webperson@yourbrokerage.com"
          autoComplete="off"
          aria-label="Web person's email"
        />
        <button type="submit" className="showcase-setup__link" disabled={!email.trim() || sendGuide.isPending}>
          {sendGuide.isPending ? "Sending…" : "Send"}
        </button>
      </form>
      {sentTo ? (
        <p className="showcase-setup__sent" role="status">
          Sent to {sentTo}. Your websites shows Live once they publish&nbsp;it.
        </p>
      ) : null}
    </div>
  );
};

const SetupInstall = ({ brokerageId, brokerageName, showcase }) => {
  const updateSite = useUpdateShowcaseSite(brokerageId);
  const sites = showcase.sites || [];
  const [siteId, setSiteId] = useState(sites[0]?.id || null);
  const site = sites.find((item) => item.id === siteId) || sites[0] || null;
  const target = site || { host: "our website", pageUrl: null };
  const [platformKey, setPlatformKey] = useState(null);
  const platform = platformOf(platformKey || site?.platform || "other");
  const [frame, setFrame] = useState(false);
  const siteOptions = sites.map((item) => ({ value: item.id, label: item.host }));

  const choosePlatform = (key) => {
    setPlatformKey(key);
    if (site && site.platform !== key) updateSite.mutate({ siteId: site.id, platform: key }, { onError: (error) => toast.error(error.message) });
  };

  const lineCode = frame ? showcase.frameScript : showcase.embedScript;

  return (
    <div className="showcase-setup__part">
      <h5 className="showcase-setup__subhead">Add it to your&nbsp;site</h5>
      {sites.length > 1 ? (
        <div className="showcase-setup__field">
          <label htmlFor="install-site">Install on</label>
          <SelectDropDown
            id="install-site"
            classNamePrefix="select"
            options={siteOptions}
            value={siteOptions.find((option) => option.value === site.id)}
            styles={PICKER_STYLES}
            errorClass="d-none"
            onChange={(option) => option && setSiteId(option.value)}
          />
        </div>
      ) : null}
      <div className="showcase-setup__field">
        <label htmlFor="install-platform">Website builder</label>
        <SelectDropDown
          id="install-platform"
          classNamePrefix="select"
          options={PLATFORM_OPTIONS}
          value={PLATFORM_OPTIONS.find((option) => option.value === platform.key)}
          styles={PICKER_STYLES}
          errorClass="d-none"
          onChange={(option) => option && choosePlatform(option.value)}
        />
      </div>
      {!showcase.checklist?.subscribed ? (
        <p className="showcase-setup__hint">You can install it now. It stays blank on your site until you&nbsp;subscribe.</p>
      ) : null}

      {platform.method === "support" ? (
        <>
          <p className="showcase-setup__hint">{platform.label} adds embeds for you. Send their support team this&nbsp;email.</p>
          <SupportEmail
            platform={platform}
            showcase={showcase}
            site={target}
            brokerageName={brokerageName}
            code={showcase.iframeCode}
          />
        </>
      ) : null}

      {platform.method === "plugin" ? (
        <>
          <p className="showcase-setup__hint">Install our WordPress&nbsp;plugin.</p>
          <Steps steps={PLUGIN_STEPS} />
          <a className="showcase-setup__link" href={showcase.wordpressPluginUrl} download>
            Download the plugin
          </a>
          <p className="showcase-setup__hint showcase-setup__hint--after">Your key</p>
          <CodeBox code={showcase.wordpressKey || showcase.publicKey} label="Copy key" />
          {platform.supportEmail ? (
            <>
              <h5 className="showcase-setup__subhead">No WordPress admin&nbsp;access?</h5>
              <SupportEmail
                platform={platform}
                showcase={showcase}
                site={target}
                brokerageName={brokerageName}
                code={showcase.embedScript}
              />
            </>
          ) : null}
        </>
      ) : null}

      {platform.method === "line" ? (
        <>
          <CodeBox code={lineCode} />
          <p className="showcase-setup__hint showcase-setup__hint--after">
            {frame
              ? "The frame version keeps its own fonts. Use it only if your builder strips the regular line."
              : "The cards take the fonts and colors of the page around them."}{" "}
            <button type="button" className="showcase-setup__quiet" onClick={() => setFrame(!frame)}>
              {frame ? "Use the regular line" : "Use the frame version"}
            </button>
          </p>
          <Steps {...(LINE_STEPS[platform.key] || LINE_STEPS.other)} />
        </>
      ) : null}

      {platform.method === "hosted" ? <HostedOptions brokerageId={brokerageId} showcase={showcase} /> : null}

      {platform.method === "line" || platform.method === "plugin" ? <GuideEmail brokerageId={brokerageId} /> : null}
    </div>
  );
};

export default SetupInstall;
